(() => {
  'use strict';

  console.log('Pink Fink frame preview loaded');

  const previewStates = [];

  /* =========================================================
   * GENERAL HELPERS
   * ========================================================= */

  const normaliseOptionValue = (value) => {
    return String(value || '')
      .trim()
      .toLowerCase();
  };

  const getProductOptionSelects = (
    root = document
  ) => {
    const searchArea =
      root &&
      typeof root.querySelectorAll === 'function'
        ? root
        : document;

    let selects = [
      ...searchArea.querySelectorAll(
        'select[id^="SingleOptionSelector"]'
      )
    ];

    /*
     * Expanse may place selectors outside the immediate
     * product root, so use the document as a fallback.
     */
    if (
      selects.length === 0 &&
      searchArea !== document
    ) {
      selects = [
        ...document.querySelectorAll(
          'select[id^="SingleOptionSelector"]'
        )
      ];
    }

    return selects;
  };

  const getSelectOptionValues = (select) => {
    if (!(select instanceof HTMLSelectElement)) {
      return [];
    }

    /*
     * Read both option.value and visible text because some
     * Shopify themes use different content in each.
     */
    return [...select.options].flatMap(
      (option) => {
        return [
          normaliseOptionValue(
            option.value
          ),
          normaliseOptionValue(
            option.textContent
          )
        ];
      }
    );
  };

  /* =========================================================
   * PRODUCT OPTION DETECTION
   * ========================================================= */

  const isFrameSelect = (select) => {
    if (!(select instanceof HTMLSelectElement)) {
      return false;
    }

    const optionValues =
      getSelectOptionValues(select);

    const hasPlainOption =
      optionValues.some(
        (optionValue) =>
          optionValue.includes('unframed') ||
          optionValue.includes('no frame') ||
          optionValue.includes('rolled') ||
          optionValue.includes('unstretched')
      );

    const hasNatural =
      optionValues.some(
        (optionValue) =>
          optionValue.includes('natural')
      );

    const hasBlack =
      optionValues.some(
        (optionValue) =>
          optionValue.includes('black')
      );

    const hasWhite =
      optionValues.some(
        (optionValue) =>
          optionValue.includes('white')
      );

    return (
      hasPlainOption &&
      hasNatural &&
      hasBlack &&
      hasWhite
    );
  };

  const isSizeSelect = (select) => {
    if (!(select instanceof HTMLSelectElement)) {
      return false;
    }

    if (isFrameSelect(select)) {
      return false;
    }

    return [...select.options].some(
      (option) => {
        const optionValue =
          option.value ||
          option.textContent ||
          '';

        return /\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?/i.test(
          optionValue
        );
      }
    );
  };

  const getFrameSelect = (
    root = document
  ) => {
    const selects =
      getProductOptionSelects(root);

    return (
      selects.find((select) =>
        isFrameSelect(select)
      ) || null
    );
  };

  const getSizeSelect = (
    root = document
  ) => {
    const selects =
      getProductOptionSelects(root);

    return (
      selects.find((select) =>
        isSizeSelect(select)
      ) || null
    );
  };

  const getSelectedFrameValue = (
    root = document
  ) => {
    const frameSelect =
      getFrameSelect(root);

    if (!frameSelect) {
      return 'Unframed';
    }

    return (
      frameSelect.value ||
      frameSelect.selectedOptions?.[0]
        ?.textContent ||
      'Unframed'
    );
  };

  const getSelectedSizeValue = (
    root = document
  ) => {
    const sizeSelect =
      getSizeSelect(root);

    if (!sizeSelect) {
      return '';
    }

    return (
      sizeSelect.value ||
      sizeSelect.selectedOptions?.[0]
        ?.textContent ||
      ''
    );
  };

  /* =========================================================
   * PRODUCT TAG HELPERS
   * ========================================================= */

  const getProductTags = (slide) => {
    return String(
      slide.dataset.pinkFinkProductTags || ''
    )
      .toLowerCase()
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  };

  /*
   * Reads tags such as:
   *
   * frame-ratio-80x90
   * frame-ratio-61x91
   * frame-ratio-100x100
   */
  const getTaggedArtworkRatio = (
    productTags
  ) => {
    const ratioTag =
      productTags.find((tag) =>
        /^frame-ratio-\d+(?:\.\d+)?x\d+(?:\.\d+)?$/.test(
          tag
        )
      );

    if (!ratioTag) {
      return null;
    }

    const dimensions =
      ratioTag.match(
        /^frame-ratio-(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/
      );

    if (!dimensions) {
      return null;
    }

    const width =
      Number(dimensions[1]);

    const height =
      Number(dimensions[2]);

    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width <= 0 ||
      height <= 0
    ) {
      return null;
    }

    return width / height;
  };

  /* =========================================================
   * GALLERY HELPERS
   * ========================================================= */

  const syncPinkFinkThumbnailHeight = (
    slide
  ) => {
    const photos =
      slide.closest('.product__photos');

    const mainPhotos =
      photos?.querySelector(
        '.product__main-photos'
      );

    const thumbnails =
      photos?.querySelector(
        '.product__thumbs:not(.product__thumbs--below)'
      );

    const scroller =
      thumbnails?.querySelector(
        '.product__thumbs--scroller'
      );

    if (
      !photos ||
      !mainPhotos ||
      !thumbnails ||
      !scroller
    ) {
      return;
    }

    if (window.innerWidth <= 798) {
      thumbnails.style.removeProperty(
        'height'
      );

      thumbnails.style.removeProperty(
        'max-height'
      );

      scroller.style.removeProperty(
        'height'
      );

      scroller.style.removeProperty(
        'max-height'
      );

      scroller.style.removeProperty(
        'overflow-y'
      );

      scroller.style.removeProperty(
        'overflow-x'
      );

      return;
    }

    const galleryHeight =
      Math.round(
        mainPhotos
          .getBoundingClientRect()
          .height
      );

    if (galleryHeight <= 0) {
      return;
    }

    thumbnails.style.height =
      `${galleryHeight}px`;

    thumbnails.style.maxHeight =
      `${galleryHeight}px`;

    scroller.style.height =
      `${galleryHeight}px`;

    scroller.style.maxHeight =
      `${galleryHeight}px`;

    scroller.style.overflowY =
      'auto';

    scroller.style.overflowX =
      'hidden';
  };

  const switchGalleryToFirstImage = (
    slide
  ) => {
    const photos =
      slide.closest('.product__photos');

    if (!photos) {
      return;
    }

    const firstThumbnail =
      photos.querySelector(
        [
          '.product__thumb-item a[data-index="0"]',
          '.product__thumb[data-index="0"]',
          '[data-product-thumb][data-index="0"]',
          '.product__thumb-item:first-child a',
          '.product__thumbs--scroller a:first-child'
        ].join(', ')
      );

    if (
      firstThumbnail instanceof HTMLElement
    ) {
      firstThumbnail.click();
    }
  };

  /* =========================================================
   * STAGE SHAPE AND ASPECT RATIO
   * ========================================================= */

  const setStageOrientation = (
    state,
    orientation,
    displayedWidth,
    displayedHeight
  ) => {
    state.stage.classList.remove(
      'is-portrait',
      'is-landscape',
      'is-square',
      'is-square-frame-override'
    );

    state.stage.classList.add(
      `is-${orientation}`
    );

    state.stage.style.aspectRatio =
      `${displayedWidth} / ${displayedHeight}`;
  };

  const applySelectedAspectRatio = (
    state
  ) => {
    const selectedSize =
      getSelectedSizeValue(
        state.root
      );

    /*
     * CANVAS PRINTS
     *
     * Canvas products have a Size selector.
     */
    if (selectedSize) {
      const normalisedSize =
        String(selectedSize)
          .toLowerCase()
          .replace(/×/g, 'x')
          .replace(/\s+/g, '');

      const dimensions =
        normalisedSize.match(
          /(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)/
        );

      if (dimensions) {
        const firstDimension =
          Number(dimensions[1]);

        const secondDimension =
          Number(dimensions[2]);

        if (
          Number.isFinite(
            firstDimension
          ) &&
          Number.isFinite(
            secondDimension
          ) &&
          firstDimension > 0 &&
          secondDimension > 0
        ) {
          const isSquare =
            Math.abs(
              firstDimension -
              secondDimension
            ) < 0.01;

          const smallerDimension =
            Math.min(
              firstDimension,
              secondDimension
            );

          const largerDimension =
            Math.max(
              firstDimension,
              secondDimension
            );

          /*
           * Square canvas products remain a true square.
           */
          if (isSquare) {
            setStageOrientation(
              state,
              'square',
              1,
              1
            );

            return;
          }

          /*
           * Landscape canvas.
           */
          if (
            state.detectedOrientation ===
            'landscape'
          ) {
            setStageOrientation(
              state,
              'landscape',
              largerDimension,
              smallerDimension
            );

            return;
          }

          /*
           * Portrait canvas.
           */
          setStageOrientation(
            state,
            'portrait',
            smallerDimension,
            largerDimension
          );

          return;
        }
      }

      console.warn(
        'Pink Fink could not read the selected size:',
        selectedSize
      );
    }

    /*
     * OIL PAINTINGS
     *
     * Oil paintings have no Size selector.
     */

    if (
      state.artworkOrientation === 'square'
    ) {
      const artworkRatio =
        Number.isFinite(
          state.artworkAspectRatio
        ) &&
        state.artworkAspectRatio > 0
          ? state.artworkAspectRatio
          : 1;

      /*
       * force-square-frame selects the square frame PNG,
       * while frame-ratio-80x90 controls the actual shape.
       */
      setStageOrientation(
        state,
        'square',
        artworkRatio,
        1
      );

      if (state.forceSquare) {
        state.stage.classList.add(
          'is-square-frame-override'
        );
      }

      return;
    }

    if (
      state.artworkOrientation ===
      'landscape'
    ) {
      const landscapeRatio =
        Number.isFinite(
          state.artworkAspectRatio
        ) &&
        state.artworkAspectRatio > 1
          ? state.artworkAspectRatio
          : 3 / 2;

      setStageOrientation(
        state,
        'landscape',
        landscapeRatio,
        1
      );

      return;
    }

    const portraitRatio =
      Number.isFinite(
        state.artworkAspectRatio
      ) &&
      state.artworkAspectRatio > 0 &&
      state.artworkAspectRatio < 1
        ? state.artworkAspectRatio
        : 2 / 3;

    setStageOrientation(
      state,
      'portrait',
      portraitRatio,
      1
    );
  };

  /* =========================================================
   * FRAME SELECTION
   * ========================================================= */

  const selectFrameUrl = (
    state,
    frameValue
  ) => {
    const isLandscape =
      state.stage.classList.contains(
        'is-landscape'
      );

    const isSquare =
      state.stage.classList.contains(
        'is-square'
      );

    const isBlack =
      frameValue.includes('black');

    const isWhite =
      frameValue.includes('white');

    if (isLandscape) {
      if (isBlack) {
        return (
          state.blackLandscapeFrameUrl
        );
      }

      if (isWhite) {
        return (
          state.whiteLandscapeFrameUrl
        );
      }

      return (
        state.naturalLandscapeFrameUrl
      );
    }

    if (isSquare) {
      if (isBlack) {
        return state.blackSquareFrameUrl;
      }

      if (isWhite) {
        return state.whiteSquareFrameUrl;
      }

      return state.naturalSquareFrameUrl;
    }

    if (isBlack) {
      return state.blackFrameUrl;
    }

    if (isWhite) {
      return state.whiteFrameUrl;
    }

    return state.naturalFrameUrl;
  };

  const applyFrameSelection = (
    state,
    selectedValue
  ) => {
    const {
      stage,
      frame,
      artworkLayer,
      innerShadow
    } = state;

    const frameValue =
      normaliseOptionValue(
        selectedValue
      );

    const isUnframed =
      frameValue === '' ||
      frameValue.includes('unframed') ||
      frameValue.includes('no frame') ||
      frameValue.includes('rolled') ||
      frameValue.includes('unstretched');

    /*
     * Use cover for all artwork.
     *
     * Square canvas products stay square without squashing.
     * Oil paintings use a stage matching their tagged ratio,
     * so cover will not force the frame itself to be square.
     */
    const artworkBackgroundSize =
      'cover';

    if (isUnframed) {
      frame.style.setProperty(
        'display',
        'none',
        'important'
      );

      /*
       * With no frame selected, allow the artwork to occupy
       * the complete stage.
       */
      stage.style.setProperty(
        '--frame-inset',
        '0%'
      );

      artworkLayer.style.setProperty(
        'background-size',
        artworkBackgroundSize,
        'important'
      );

      artworkLayer.style.setProperty(
        'background-color',
        'transparent',
        'important'
      );

      artworkLayer.style.setProperty(
        'background-position',
        'center center',
        'important'
      );

      artworkLayer.style.setProperty(
        'box-shadow',
        'none',
        'important'
      );

      innerShadow.style.setProperty(
        'display',
        'none',
        'important'
      );

      return;
    }

    const selectedFrameUrl =
      selectFrameUrl(
        state,
        frameValue
      );

    if (!selectedFrameUrl) {
      console.warn(
        'Pink Fink frame URL was not found.',
        {
          frameValue,
          stageClasses:
            state.stage.className
        }
      );

      return;
    }

    frame.src = selectedFrameUrl;

    frame.style.removeProperty(
      'display'
    );

    stage.style.removeProperty(
      '--frame-inset'
    );

    artworkLayer.style.setProperty(
      'background-size',
      artworkBackgroundSize,
      'important'
    );

    artworkLayer.style.setProperty(
      'background-color',
      'transparent',
      'important'
    );

    artworkLayer.style.setProperty(
      'background-position',
      'center center',
      'important'
    );

    artworkLayer.style.setProperty(
      'box-shadow',
      'none',
      'important'
    );

    innerShadow.style.setProperty(
      'display',
      'none',
      'important'
    );
  };

  /* =========================================================
   * PREVIEW ACTIVATION
   * ========================================================= */

  const activatePreview = (state) => {
    const selectedFrameValue =
      getSelectedFrameValue(
        state.root
      );

    /*
     * Set the orientation first because the chosen frame
     * file depends on portrait, landscape or square.
     */
    applySelectedAspectRatio(state);

    applyFrameSelection(
      state,
      selectedFrameValue
    );

    state.composite.style.removeProperty(
      'display'
    );

    state.slide.classList.add(
      'pink-fink-ready'
    );

    window.requestAnimationFrame(() => {
      switchGalleryToFirstImage(
        state.slide
      );

      syncPinkFinkThumbnailHeight(
        state.slide
      );
    });
  };

  /* =========================================================
   * PREVIEW INITIALISATION
   * ========================================================= */

  const initialisePinkFinkPreview = () => {
    const slides =
      document.querySelectorAll(
        '[data-pink-fink-frame-preview]:not([data-pink-fink-initialised])'
      );

    slides.forEach((slide) => {
      slide.setAttribute(
        'data-pink-fink-initialised',
        'true'
      );

      const artworkUrl =
        slide.dataset.pinkFinkArtwork;

      const artworkAlt =
        slide.dataset
          .pinkFinkArtworkAlt || '';

      /*
       * Portrait frame files.
       */
      const naturalFrameUrl =
        slide.dataset
          .pinkFinkNaturalFrame;

      const blackFrameUrl =
        slide.dataset
          .pinkFinkBlackFrame;

      const whiteFrameUrl =
        slide.dataset
          .pinkFinkWhiteFrame;

      /*
       * Landscape frame files.
       */
      const naturalLandscapeFrameUrl =
        slide.dataset
          .pinkFinkNaturalLandscapeFrame;

      const blackLandscapeFrameUrl =
        slide.dataset
          .pinkFinkBlackLandscapeFrame;

      const whiteLandscapeFrameUrl =
        slide.dataset
          .pinkFinkWhiteLandscapeFrame;

      /*
       * Square frame files.
       */
      const naturalSquareFrameUrl =
        slide.dataset
          .pinkFinkNaturalSquareFrame;

      const blackSquareFrameUrl =
        slide.dataset
          .pinkFinkBlackSquareFrame;

      const whiteSquareFrameUrl =
        slide.dataset
          .pinkFinkWhiteSquareFrame;

      const imageMain =
        slide.querySelector(
          '[data-product-image-main]'
        );

      const root =
        slide.closest(
          '.product-section'
        ) ||
        slide.closest(
          '[data-section-type="product"]'
        ) ||
        slide.closest(
          '.product-template'
        ) ||
        slide.closest(
          '[data-section-id]'
        ) ||
        document;

      if (!artworkUrl || !imageMain) {
        console.warn(
          'Pink Fink preview could not initialise.',
          {
            artworkUrl,
            imageMain
          }
        );

        return;
      }

      const composite =
        document.createElement('div');

      composite.className =
        'pink-fink-composite';

      /*
       * Keep the normal gallery image visible until the
       * customer changes a size or frame option.
       */
      composite.style.display = 'none';

      const stage =
        document.createElement('div');

      stage.className =
        'pink-fink-composite__stage';

      const artwork = new Image();

      artwork.src = artworkUrl;
      artwork.alt = artworkAlt;
      artwork.loading = 'eager';

      const artworkLayer =
        document.createElement('div');

      artworkLayer.className =
        'pink-fink-composite__artwork-layer';

      artworkLayer.style.backgroundImage =
        `url("${artworkUrl}")`;

      artworkLayer.style.backgroundSize =
        'cover';

      artworkLayer.style.backgroundPosition =
        'center center';

      artworkLayer.style.backgroundRepeat =
        'no-repeat';

      artworkLayer.style.backgroundColor =
        'transparent';

      artworkLayer.style.boxShadow =
        'none';

      const innerShadow =
        document.createElement('span');

      innerShadow.className =
        'pink-fink-composite__inner-shadow';

      innerShadow.setAttribute(
        'aria-hidden',
        'true'
      );

      innerShadow.style.setProperty(
        'display',
        'none',
        'important'
      );

      const frame =
        document.createElement('img');

      frame.className =
        'pink-fink-composite__frame';

      frame.alt = '';

      frame.setAttribute(
        'aria-hidden',
        'true'
      );

      frame.style.setProperty(
        'display',
        'none',
        'important'
      );

      let previewFinished = false;

      const finishPreview = () => {
        if (previewFinished) {
          return;
        }

        previewFinished = true;

        const naturalWidth =
          artwork.naturalWidth;

        const naturalHeight =
          artwork.naturalHeight;

        const detectedArtworkAspectRatio =
          naturalWidth / naturalHeight;

        const detectedOrientation =
          Math.abs(
            detectedArtworkAspectRatio - 1
          ) <= 0.05
            ? 'square'
            : detectedArtworkAspectRatio > 1
              ? 'landscape'
              : 'portrait';

        const productTags =
          getProductTags(slide);

        const taggedArtworkRatio =
          getTaggedArtworkRatio(
            productTags
          );

        /*
         * A frame-ratio tag overrides the image-file ratio.
         */
        const artworkAspectRatio =
          taggedArtworkRatio ||
          detectedArtworkAspectRatio;

        const forceSquare =
          productTags.includes(
            'force-square-frame'
          );

        const forcePortrait =
          productTags.includes(
            'force-portrait-frame'
          );

        const forceLandscape =
          productTags.includes(
            'force-landscape-frame'
          );

        const isSquareArtwork =
          Math.abs(
            artworkAspectRatio - 1
          ) <= 0.05;

        let artworkOrientation;

        if (forceSquare) {
          artworkOrientation =
            'square';
        } else if (forcePortrait) {
          artworkOrientation =
            'portrait';
        } else if (forceLandscape) {
          artworkOrientation =
            'landscape';
        } else if (isSquareArtwork) {
          artworkOrientation =
            'square';
        } else if (
          artworkAspectRatio > 1
        ) {
          artworkOrientation =
            'landscape';
        } else {
          artworkOrientation =
            'portrait';
        }

        /*
         * Set the initial orientation.
         */
        if (
          artworkOrientation === 'square'
        ) {
          stage.classList.add(
            'is-square'
          );

          stage.style.aspectRatio =
            `${artworkAspectRatio} / 1`;

          if (forceSquare) {
            stage.classList.add(
              'is-square-frame-override'
            );
          }
        } else if (
          artworkOrientation ===
          'landscape'
        ) {
          stage.classList.add(
            'is-landscape'
          );

          stage.style.aspectRatio =
            `${artworkAspectRatio} / 1`;
        } else {
          stage.classList.add(
            'is-portrait'
          );

          stage.style.aspectRatio =
            `${artworkAspectRatio} / 1`;
        }

        stage.append(
          artworkLayer,
          innerShadow,
          frame
        );

        composite.appendChild(stage);
        imageMain.appendChild(composite);

        previewStates.push({
          slide,
          root,
          composite,
          stage,
          frame,
          artworkLayer,
          innerShadow,

          detectedOrientation,
          artworkOrientation,
          artworkAspectRatio,

          forceSquare,
          forcePortrait,
          forceLandscape,

          naturalFrameUrl,
          blackFrameUrl,
          whiteFrameUrl,

          naturalLandscapeFrameUrl,
          blackLandscapeFrameUrl,
          whiteLandscapeFrameUrl,

          naturalSquareFrameUrl,
          blackSquareFrameUrl,
          whiteSquareFrameUrl
        });
      };

      artwork.addEventListener(
        'load',
        finishPreview,
        {
          once: true
        }
      );

      artwork.addEventListener(
        'error',
        () => {
          console.error(
            'Pink Fink artwork failed to load:',
            artworkUrl
          );
        },
        {
          once: true
        }
      );

      if (
        artwork.complete &&
        artwork.naturalWidth > 0
      ) {
        finishPreview();
      }
    });
  };

  /* =========================================================
   * PRODUCT OPTION CHANGE HANDLING
   * ========================================================= */

  const handlePinkFinkSelectorChange = (
    event
  ) => {
    const control =
      event.target;

    /*
     * Avoid .closest() on event.target because old theme
     * code is already producing a closest() error.
     */
    if (
      !(
        control instanceof
        HTMLSelectElement
      )
    ) {
      return;
    }

    const optionSelects =
      getProductOptionSelects(
        document
      );

    if (
      !optionSelects.includes(
        control
      )
    ) {
      return;
    }

    /*
     * Ignore unrelated product dropdowns.
     */
    if (
      !isFrameSelect(control) &&
      !isSizeSelect(control)
    ) {
      return;
    }

    /*
     * Give Shopify a moment to update the selected value.
     */
    window.setTimeout(() => {
      previewStates.forEach(
        (state) => {
          if (
            state.root !== document &&
            !state.root.contains(
              control
            )
          ) {
            return;
          }

          activatePreview(state);
        }
      );
    }, 100);
  };

  document.addEventListener(
    'change',
    handlePinkFinkSelectorChange,
    true
  );

  document.addEventListener(
    'DOMContentLoaded',
    initialisePinkFinkPreview
  );

  document.addEventListener(
    'shopify:section:load',
    initialisePinkFinkPreview
  );

  initialisePinkFinkPreview();

  /* =========================================================
   * WINDOW RESIZING
   * ========================================================= */

  let pinkFinkResizeTimer;

  window.addEventListener(
    'resize',
    () => {
      window.clearTimeout(
        pinkFinkResizeTimer
      );

      pinkFinkResizeTimer =
        window.setTimeout(() => {
          document
            .querySelectorAll(
              '[data-pink-fink-frame-preview].pink-fink-ready'
            )
            .forEach((slide) => {
              syncPinkFinkThumbnailHeight(
                slide
              );
            });
        }, 150);
    }
  );
})();